import React, { useEffect, useRef, useState } from "react";

import Map, { NavigationControl } from "react-map-gl";
import mapboxgl from "mapbox-gl";
import { Search, Menu, X, Info } from "lucide-react";
import ShipMarker from "./components/ShipMarker";
import "mapbox-gl/dist/mapbox-gl.css";
import ControlPanel from "./components/ControlPanel";
import {
  getPortPositions,
  getFinalPositions,
  getFinalPositionsPublic,
  getMessages,
  getTopTenFastestShips,
  getTopTenFuelConsumption,
} from "./data";
import { ShipPosition } from "./types/ShipPosition";
import { PortPosition } from "./types/PortPosition";
import SearchBar from "./components/SearchBar";
import { ShipMessage } from "./types/ShipMessage";
import NavBar from "./components/NavBar";
import CruiseLineFilter from "./components/CruiseLineFilter";
import GoogleAdSidebar from "./components/GoogleAd";
import { Routes, Route, useLocation } from "react-router-dom";
import BlogList from "./pages/BlogList";
import BlogPost from "./pages/BlogPost";
import About from "./pages/About";
import Home from "./pages/Home";
import Footer from "./components/Footer";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Fleet from "./pages/Fleet";
import RaceMode from "./pages/RaceMode";
import Captain from "./pages/Captain";
// import BridgePOC from "./pages/BridgePOC"; // POC — uncomment to enable
import { useAuth } from "@clerk/react";
import { isAdminUser } from "./data/adminUsers";

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { isSignedIn, isLoaded, getToken, has, userId } = useAuth();
  const isCaptain = isLoaded && (has({ plan: "vessel_vibes_captain" }) || isAdminUser(userId));
  const [marineData, setMarineData] = useState<ShipPosition[] | null>(null);
  const [portData, setPortData] = useState<PortPosition[] | null>(null);
  const [messageData, setMessages] = useState<ShipMessage[] | null>(null);
  const [fastShipsData, setFastestShips] = useState<ShipPosition[] | null>(
    null
  );
  const [fuelConsumptionData, setFuelConsumption] = useState<
    ShipPosition[] | null
  >(null);
  const [avgSpeed, setAvgSpeed] = useState(
    +(Math.random() * (13.9 - 11) + 11).toFixed(1)
  );
  const [selectedLines, setSelectedLines] = useState<string[]>([]);
  const hasInitializedLines = useRef(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [lineSearchTerm, setLineSearchTerm] = useState("");

  const [webGLSupported] = useState(() => mapboxgl.supported());

  const [viewState, setViewState] = useState({
    longitude: 0,
    latitude: 20,
    zoom: 2,
  });

  useEffect(() => {
    // Wait for Clerk to finish loading before deciding which data to fetch
    if (!isLoaded) return;

    const fetchAllData = async () => {
      if (isSignedIn && isCaptain) {
        // Captain: fetch full data through auth proxy with Clerk JWT
        const token = await getToken();
        if (!token) return;
        const [ships, ports, messages, fastest, fuel] = await Promise.all([
          getFinalPositions(token),
          getPortPositions(),
          getMessages(token),
          getTopTenFastestShips(token),
          getTopTenFuelConsumption(token),
        ]);
        setMarineData(ships);
        setPortData(ports);
        setMessages(messages);
        setFastestShips(fastest);
        setFuelConsumption(fuel);
        if (!hasInitializedLines.current) {
          const lines = Array.from(new Set(ships.map((ship) => ship.LINE).filter(Boolean)));
          setSelectedLines(lines);
          hasInitializedLines.current = true;
        }
      } else {
        // Unauthenticated: positions and ports only, no fuel/speed/rankings/messages
        const [ships, ports] = await Promise.all([
          getFinalPositionsPublic(),
          getPortPositions(),
        ]);
        setMarineData(ships);
        setPortData(ports);
        setMessages(null);
        setFastestShips(null);
        setFuelConsumption(null);
        if (!hasInitializedLines.current) {
          const lines = Array.from(new Set(ships.map((ship) => ship.LINE).filter(Boolean)));
          setSelectedLines(lines);
          hasInitializedLines.current = true;
        }
      }
    };

    fetchAllData();

    const interval = setInterval(fetchAllData, 1 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isLoaded, isSignedIn, isCaptain]);

  // Scroll to top on every route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Handle focus on a ship when navigating from Fleet page
  useEffect(() => {
    const focusShip = (location.state as any)?.focusShip;
    if (focusShip) {
      setViewState({ latitude: focusShip.lat, longitude: focusShip.lon, zoom: 6 });
    }
  }, [location.state]);

  const MAPBOX_TOKEN =
    "pk.eyJ1Ijoia3JlYnM1NDAiLCJhIjoiY20ybWx5Z2N4MHBuaDJpbjQ4b3NhZWVwdCJ9.cweglTY7M4aK2P4OBvE08Q";

  return (
    <div className="h-screen w-full bg-[#5D767E] text-white relative">
      {/* Navigation Bar */}
      <NavBar isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      <Routes>
        <Route
          path="/"
          element={
            <>
              {/* Search Bar */}
              <div className="flex-1">
                {/* Cruise Line Filter Toggle & Panel */}
                <CruiseLineFilter
                  marineData={marineData}
                  selectedLines={selectedLines}
                  setSelectedLines={setSelectedLines}
                  showFilterPanel={showFilterPanel}
                  setShowFilterPanel={setShowFilterPanel}
                  lineSearchTerm={lineSearchTerm}
                  setLineSearchTerm={setLineSearchTerm}
                />
                <SearchBar
                  marineData={marineData}
                  portData={portData}
                  onSelectLocation={setViewState}
                />
              </div>

              {/* Ads container - visible on md+ only */}
              {/* <GoogleAdSidebar/> */}

              {/* Map */}
              {webGLSupported ? (
                <Map
                  mapboxAccessToken={MAPBOX_TOKEN}
                  {...viewState}
                  onMove={(evt) => setViewState(evt.viewState)}
                  style={{ width: "100%", height: "100%" }}
                  mapStyle="mapbox://styles/mapbox/navigation-night-v1"
                >
                  <NavigationControl position="bottom-right" />

                  {/* Ship Markers */}
                  {marineData
                    ?.filter((ship) => selectedLines.includes(ship.LINE))
                    .map((ship) => (
                      <ShipMarker
                        key={`${ship.SHIPNAME}-${ship.LINE}`}
                        ship={ship}
                      />
                    ))}
                </Map>
              ) : (
                <div className="flex items-center justify-center w-full h-full bg-[#080e1a]">
                  <div className="text-center max-w-sm px-6">
                    <div className="text-4xl mb-4">🗺️</div>
                    <h2 className="text-white text-lg font-semibold mb-2">Map unavailable</h2>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      Your browser needs graphics acceleration enabled to load the map. Here's how to fix it in Chrome:
                    </p>
                    <ol className="text-gray-400 text-sm leading-relaxed mt-3 text-left space-y-1 list-none">
                      <li><span className="text-white font-medium">1.</span> Open <span className="text-blue-400 font-mono">chrome://settings/system</span></li>
                      <li><span className="text-white font-medium">2.</span> Turn on <span className="text-white font-medium">"Use graphics acceleration when available"</span></li>
                      <li><span className="text-white font-medium">3.</span> Click <span className="text-white font-medium">Relaunch</span></li>
                      <li><span className="text-white font-medium">4.</span> Return to this page</li>
                    </ol>
                    <p className="text-gray-500 text-xs mt-4">Not using Chrome? Try opening this site in Chrome or Edge.</p>
                  </div>
                </div>
              )}

              {/* Info Panel */}
              <ControlPanel
                controlPanel={{
                  shipCount: marineData?.length || 54,
                  avgSpeed: avgSpeed,
                  portCount: portData?.length || 105,
                  messageData: messageData,
                  fastShipsData: fastShipsData,
                  fuelConsumptionData: fuelConsumptionData,
                }}
              />
            </>
          }
        />

        {/* <Route path="/home" element={<Home />} /> */}
        {/* <Route path="/blog" element={<BlogList />} /> */}
        {/* <Route path="/blog/:slug" element={<BlogPost />} /> */}
        <Route path="/fleet" element={<Fleet />} />
        <Route path="/race" element={<RaceMode />} />
        <Route path="/captain" element={<Captain />} />
        {/* <Route path="/bridge" element={<BridgePOC />} /> */}{/* POC — uncomment to enable */}
        <Route path="/about" element={<About />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
      </Routes>
      <Footer />
    </div>
  );
}

export default App;
